/*
 * File: ShareStoryFlow.styles.js
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 7, 2022 at 1:53 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: May 18, 2025 at 1:32 AM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import styled, { css } from 'styled-components';

// Components
import { IconButton } from '../IconButton';
import { Card } from '../Card';
import { Padding } from '../Padding';

// Constants
import {
	AnimationPanelWidth, ShareTopbarHeight, xLargeBreakpoint
} from '../../styles/constants';


/**
 * Constants
 */

const ANIMATION_DURATION = '1s';


/**
 * Styles
 */

export const Wrapper = styled.div`
	width: 100%;
	height: 100%;
	display: flex;
`;

export const ContainerPanel = styled.div`
	height: 100%;
	flex-grow: 1;
	position: relative;
	padding: 0px;

	@media only screen and (min-width: ${xLargeBreakpoint}em) {
		padding-left: ${AnimationPanelWidth}px;
	}
`;

export const Header = styled.div`
	display: flex;
	height: ${ShareTopbarHeight}px;
	align-items: center;
	padding: 10px 0px;
	position: fixed;
	background-color: ${({ theme }) => theme.primaryBackground};
	z-index: 9;
	width: 100%;

	@media only screen and (min-width: ${xLargeBreakpoint}em) {
		width: calc(100vw - ${AnimationPanelWidth}px);
	}
	${({ $isScrolled, theme }) => ($isScrolled ? css`
		box-shadow: ${theme.navigationShadow};
	` : css`
		box-shadow: none;
	`)};
`;

export const ShareFooter = styled(Padding)`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-wrap: wrap;
	gap: 12px;
	padding-top: 30px;
	padding-bottom: 20px;
`;

export const HeaderCard = styled(Card)`
	padding: 8px 18px;
	border-radius: 4px;
	align-items: center;
	cursor: pointer;
	position: relative;
`;

export const HeaderCardPadding = styled(Padding)`
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	margin: ${ShareTopbarHeight + 1}px 0px -${ShareTopbarHeight + 1}px;
	z-index: 1;
`;

export const HeaderModule = styled.div`
	display: flex;
	align-items: center;

	${({ $fullWidth }) => $fullWidth && css`
		justify-content: center;
		flex: 1;
	`}
`;

export const LockButton = styled(IconButton)`
	color: ${({ theme }) => theme.primaryButton};
`;

export const AnimationPanel = styled.div`
	width: ${AnimationPanelWidth}px;
	height: 100%;
	position: fixed;
	justify-content: center;
	flex-shrink: 0;
	display: none;

	@media only screen and (min-width: ${xLargeBreakpoint}em) {
		display: flex;
	}
`;

export const StepContainer = styled.div`
	position: relative;
	min-height: 100%;
`;

export const Step = styled.div`
	opacity: 0;
	visibility: hidden;
	pointer-events: none;
	min-height: calc(100vh - ${ShareTopbarHeight}px);
	width: 100%;
	position: absolute;
	top: ${ShareTopbarHeight}px;
	display: flex;
	flex-direction: column;

	& > div {
		flex-grow: 1;
	}
	&.show {
		opacity: 1;
		visibility: visible;
		transition-delay: 0.3s;
		position: absolute;
		pointer-events: initial;
	}
`;


/**
 * Sky Components
 */

export const SkyContainer = styled.div`
	position: absolute;
	top: 0px;
	left: 0px;
	width: 100%;
	height: 100%;

	& > div {
		position: absolute;
		top: 0px;
		left: 0px;
		width: 100%;
		height: 100%;
		transition: all ${ANIMATION_DURATION} ease-in-out;
	}
	&.step1 {
		background: linear-gradient(0deg, rgba(187, 60, 97, 1) 0%, rgba(21, 52, 98, 1) 60%);
	}
	&.step2 {
		background: linear-gradient(0deg,rgba(194, 138, 93, 1) 0%, rgba(217, 121, 100, 1) 16%, rgba(21, 52, 98, 1) 82%);
	}
	&.step3 {
		background: linear-gradient(0deg,rgba(207, 170, 112, 1) 0%, rgba(230, 117, 92, 1) 28%, rgba(61, 79, 130, 1) 82%);
	}
	&.step4 {
		background: linear-gradient(0deg,rgba(207, 170, 112, 1) 0%, rgba(230, 117, 92, 1) 28%, rgba(61, 79, 130, 1) 82%);
	}
	&.step5 {
		background: linear-gradient(0deg,rgba(234, 162, 136, 1) 0%, rgba(244, 223, 209, 1) 29%, rgba(61, 79, 130, 1) 100%);
	}
	&.step6 {
		background: linear-gradient(0deg,rgba(234, 162, 136, 1) 0%, rgba(244, 223, 209, 1) 50%, rgba(61, 79, 130, 1) 100%);
	}
	&.step7 {
		background: linear-gradient(0deg,rgba(238, 189, 159, 1) 0%, rgba(253, 240, 212, 1) 40%, rgba(255, 253, 253, 1) 55%, rgba(61, 79, 130, 1) 100%);
	}
	&.step8 {
		background: linear-gradient(0deg,rgba(238, 189, 159, 1) 0%, rgba(253, 240, 212, 1) 40%, rgba(255, 253, 253, 1) 55%, rgba(134, 156, 217, 1) 100%);
	}
	&.step9 {
		background: linear-gradient(0deg,rgba(238, 189, 159, 1) 0%, rgba(255, 253, 253, 1) 50%, rgba(134, 156, 217, 1) 100%);
	}
	&.step10 {
		background: linear-gradient(0deg,rgba(255, 252, 250, 1) 0%, rgba(187, 201, 237, 1) 100%);
	}
`;

export const Sky1 = styled.div`
	background: linear-gradient(0deg, rgba(187, 60, 97, 1) 0%, rgba(21, 52, 98, 1) 60%);
	opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
`;

export const Sky2 = styled.div`
	background: linear-gradient(0deg,rgba(194, 138, 93, 1) 0%, rgba(217, 121, 100, 1) 16%, rgba(21, 52, 98, 1) 82%);
	opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
`;

export const Sky3 = styled.div`
	background: linear-gradient(0deg,rgba(207, 170, 112, 1) 0%, rgba(230, 117, 92, 1) 28%, rgba(61, 79, 130, 1) 82%);
	opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
`;

export const Sky4 = styled.div`
	background: linear-gradient(0deg,rgba(207, 170, 112, 1) 0%, rgba(230, 117, 92, 1) 28%, rgba(61, 79, 130, 1) 82%);
	opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
`;

export const Sky5 = styled.div`
	background: linear-gradient(0deg,rgba(234, 162, 136, 1) 0%, rgba(244, 223, 209, 1) 29%, rgba(61, 79, 130, 1) 100%);
	opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
`;

export const Sky6 = styled.div`
	background: linear-gradient(0deg,rgba(234, 162, 136, 1) 0%, rgba(244, 223, 209, 1) 50%, rgba(61, 79, 130, 1) 100%);
	opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
`;

export const Sky7 = styled.div`
	background: linear-gradient(0deg,rgba(238, 189, 159, 1) 0%, rgba(253, 240, 212, 1) 40%, rgba(255, 253, 253, 1) 55%, rgba(61, 79, 130, 1) 100%);
	opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
`;

export const Sky8 = styled.div`
	background: linear-gradient(0deg,rgba(238, 189, 159, 1) 0%, rgba(253, 240, 212, 1) 40%, rgba(255, 253, 253, 1) 55%, rgba(134, 156, 217, 1) 100%);
	opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
`;

export const Sky9 = styled.div`
	background: linear-gradient(0deg,rgba(238, 189, 159, 1) 0%, rgba(255, 253, 253, 1) 50%, rgba(134, 156, 217, 1) 100%);
	opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
`;

export const Sky10 = styled.div`
	background: linear-gradient(0deg,rgba(255, 252, 250, 1) 0%, rgba(187, 201, 237, 1) 100%);
	opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
`;


/**
 * Sun Components
 */

export const SunContainer = styled.div`
	width: 120px;
	position: absolute;
	z-index: 1;
	transition: all ${ANIMATION_DURATION} ease-in-out;

	&.step1 {
		height: 30px;
		bottom: 220px;
	}
	&.step2 {
		height: 70px;
		bottom: 220px;
	}
	&.step3 {
		height: 120px;
		bottom: 220px;
	}
	&.step4 {
		height: 120px;
		bottom: 290px;
	}
	&.step5 {
		height: 120px;
		bottom: 410px;
	}
	&.step6 {
		height: 120px;
		bottom: 500px;
	}
	&.step7 {
		height: 120px;
		bottom: 600px;
	}
	&.step8 {
		height: 120px;
		bottom: 660px;
	}
	&.step9 {
		height: 120px;
		bottom: 660px;
	}
`;

export const SunInnerContainer = styled.div`
	width: 120px;
	height: 120px;
	border-radius: 50%;
	transition: all ${ANIMATION_DURATION} ease-in-out;
	position: relative;

	&.step1 {
		background: linear-gradient(0deg,rgba(240, 102, 78, 1) 30%, rgba(240, 81, 78, 1) 100%);
	}
	&.step2 {
		background: linear-gradient(0deg,rgba(240, 81, 78, 1) 30%, rgba(252, 153, 86, 1) 130%);
	}
	&.step3 {
		background: linear-gradient(0deg,rgba(255, 84, 82, 1) 0%, rgba(255, 154, 87, 1) 80%);
	}
	&.step4 {
		background: linear-gradient(0deg,rgba(255, 84, 82, 1) 0%, rgba(255, 154, 87, 1) 80%);
	}
	&.step5 {
		background: linear-gradient(0deg,rgba(255, 200, 166, 1) 0%, rgba(255, 232, 219, 1) 100%);
	}
	&.step6 {
		background: linear-gradient(0deg,rgba(255, 249, 237, 1) 0%, rgba(255, 252, 247, 1) 100%);
	}
	&.step7 {
		background: linear-gradient(0deg,rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 1) 100%);
	}
	&.step8 {
		background: linear-gradient(0deg,rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 1) 100%);
	}
	&.step9 {
		background: linear-gradient(0deg,rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 1) 100%);
	}
	&.step10 {
		background: linear-gradient(0deg,rgba(255, 249, 237, 1) 0%, rgba(255, 252, 247, 1) 100%);
	}
	& > div {
		position: absolute;
		top: 0px;
		left: 0px;
		width: 100%;
		height: 100%;
		transition: all ${ANIMATION_DURATION} ease-in-out;
	}
`;

export const Sun1 = styled.div`
	background: linear-gradient(0deg,rgba(240, 102, 78, 1) 30%, rgba(240, 81, 78, 1) 100%);
	opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
	border-radius: 50%;
	box-shadow: 0px 0px 20px 2px rgba(249, 95, 93, 0.5);
`;

export const Sun2 = styled.div`
	background: linear-gradient(0deg,rgba(240, 81, 78, 1) 30%, rgba(252, 153, 86, 1) 130%);
	opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
	border-radius: 50%;
	box-shadow: 0px 0px 10px 2px rgba(249, 95, 93, 0.5);
`;

export const Sun3 = styled.div`
	background: linear-gradient(0deg,rgba(255, 128, 82, 1) 0%, rgba(255, 154, 87, 1) 80%);
	opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
	border-radius: 50%;
	box-shadow: 0px 0px 15px 5px rgba(251, 153, 111, 0.6);
`;

export const Sun4 = styled.div`
	background: linear-gradient(0deg,rgba(255, 128, 82, 1) 0%, rgba(255, 154, 87, 1) 80%);
	opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
	border-radius: 50%;
	box-shadow: 0px 0px 15px 5px rgba(251, 153, 111, 0.6);
`;

export const Sun5 = styled.div`
	background: linear-gradient(0deg,rgba(255, 200, 166, 1) 0%, rgba(255, 232, 219, 1) 100%);
	opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
	border-radius: 50%;
	box-shadow: 0px 0px 20px 12px rgba(255, 245, 232, 0.4);
`;

export const Sun6 = styled.div`
	background: linear-gradient(0deg,rgba(255, 249, 237, 1) 0%, rgba(255, 252, 247, 1) 100%);
	opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
	border-radius: 50%;
	box-shadow: 0px 0px 80px 57px rgba(255, 234, 224, 1);
`;

export const Sun7 = styled.div`
	background: linear-gradient(0deg,rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 1) 100%);
	opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
	border-radius: 50%;
	box-shadow:
        0 0 60px 30px rgba(255, 249, 237, 0.5),
        0 0 100px 60px rgba(255, 249, 237, 0.5),
        0 0 140px 90px rgba(255, 249, 237, 0.5);
`;

export const Sun8 = styled.div`
	background: linear-gradient(0deg,rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 1) 100%);
	opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
	border-radius: 50%;
	box-shadow:
        0 0 60px 30px rgba(255, 249, 237, 0.6),
        0 0 100px 60px rgba(255, 249, 237, 0.6),
        0 0 140px 90px rgba(255, 249, 237, 0.6);
`;

export const Sun9 = styled.div`
	background: linear-gradient(0deg,rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 1) 100%);
	opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
	border-radius: 50%;
	box-shadow: 0px 0px 107px 57px rgba(255, 255, 255, 1);
	box-shadow:
        0 0 60px 30px rgba(255, 249, 237, 0.7),
        0 0 100px 60px rgba(255, 249, 237, 0.7),
        0 0 140px 90px rgba(255, 249, 237, 0.7);
`;

export const Sun10 = styled.div`
	background: linear-gradient(0deg,rgba(255, 249, 237, 1) 0%, rgba(255, 252, 247, 1) 100%);
	opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
	border-radius: 50%;
	box-shadow: 0px 0px 107px 57px rgba(255, 255, 255, 1);
	box-shadow:
        0 0 60px 30px rgba(255, 249, 237, 0.7),
        0 0 100px 60px rgba(255, 249, 237, 0.7),
        0 0 140px 90px rgba(255, 249, 237, 0.7);
`;


/**
 * Sun Reflection Components
 */

export const SunReflectionContainer = styled.div`
	width: 120px;
	height: 30px;
	position: absolute;
	z-index: 2;
	overflow: hidden;
	transform: scaleY(-1);
	transition: all ${ANIMATION_DURATION} ease-in-out;

	&.step1 {
		height: 30px;
		bottom: 190px;
	}
	&.step2 {
		height: 70px;
		bottom: 150px;
	}
	&.step3 {
		height: 120px;
		bottom: 100px;
	}
	&.step4 {
		height: 120px;
		bottom: 30px;
	}
	&.step5 {
		height: 120px;
		bottom: -90px;
	}
	&.step6,
	&.step7,
	&.step8,
	&.step9 {
		bottom: -180px;
	}
`;

export const SunReflectionInnerContainer = styled.div`
	width: 120px;
	height: 120px;
	border-radius: 50%;
	opacity: 0.4;
	mask-image: repeating-linear-gradient(transparent, transparent 3px, white 3px, white 4px), linear-gradient(transparent 50%, white 90%);
	transition: all ${ANIMATION_DURATION} ease-in-out;
	position: relative;
	overflow: hidden;

	& > div {
		position: absolute;
		top: 0px;
		left: 0px;
		width: 100%;
		height: 100%;
		transition: all ${ANIMATION_DURATION} ease-in-out;
	}
	&.step1 {
		mask-image: repeating-linear-gradient(transparent, transparent 3px, white 3px, white 4px), linear-gradient(transparent 50%, white 90%);
		opacity: 0.6;
	}
	&.step2 {
		mask-image: repeating-linear-gradient(transparent, transparent 3px, white 3px, white 4px), linear-gradient(transparent 50%, white 90%);
		opacity: 0.3;
	}
	&.step3,
	&.step4 {
		mask-image: repeating-linear-gradient(transparent, transparent 3px, white 3px, white 4px), linear-gradient(transparent 50%, white 90%);
		opacity: 0.2;
	}
	&.step5 {
		opacity: 0.2;
		mask-image: repeating-linear-gradient(transparent, transparent 3px, white 3px, white 4px);
	}
	&.step6 {
		opacity: 0.1;
		mask-image: repeating-linear-gradient(transparent, transparent 3px, white 3px, white 4px);
	}
	&.step7,
	&.step8,
	&.step9,
	&.step10 {
		mask-image: repeating-linear-gradient(transparent, transparent 3px, white 3px, white 4px);
		opacity: 0;
	}
`;


/**
 * Ocean Components
 */

export const OceanContainer = styled.div`
	height: 220px;
	width: 100%;
	position: absolute;
	bottom: 0px;
	left: 0px;
	z-index: 1;

	& > div {
		position: absolute;
		top: 0px;
		left: 0px;
		width: 100%;
		height: 100%;
		transition: all ${ANIMATION_DURATION} ease-in-out;
	}
	&.step1 {
		background: linear-gradient(0deg, rgba(17, 19, 58, 1) 0%, rgba(173, 61, 106, 1) 100%);
	}
	&.step2 {
		background: linear-gradient(0deg,rgba(17, 19, 58, 1) 0%, rgba(38, 38, 82, 1) 35%, rgba(224, 108, 70, 1) 100%);
	}
	&.step3 {
		background: linear-gradient(0deg, rgba(61, 79, 130, 1) 28%, rgba(214, 120, 86, 1) 100%);
	}
	&.step4 {
		background: linear-gradient(0deg, rgba(61, 79, 130, 1) 28%, rgba(214, 120, 86, 1) 100%);
	}
	&.step5 {
		background: linear-gradient(0deg, rgba(61, 79, 130, 1) 0%, rgba(244, 223, 209, 1) 100%);
	}
	&.step6 {
		background: linear-gradient(0deg, rgba(61, 79, 130, 1) 0%, rgba(173, 194, 224, 1) 80%, rgba(244, 223, 209, 1) 100%);
	}
	&.step7 {
		background: linear-gradient(0deg, rgba(61, 79, 130, 1) 0%, rgba(173, 194, 224, 1) 60%, rgba(205, 223, 250, 1) 80%, rgba(255, 234, 219, 1) 104%);
	}
	&.step8 {
		background: linear-gradient(0deg, rgba(61, 79, 130, 1) -10%, rgba(173, 194, 224, 1) 60%, rgba(205, 223, 250, 1) 80%, rgba(255, 234, 219, 1) 120%);
	}
	&.step9 {
		background: linear-gradient(0deg, rgba(61, 79, 130, 1) -10%, rgba(173, 194, 224, 1) 60%, rgba(205, 223, 250, 1) 80%, rgba(222, 236, 255, 1) 120%);
	}
	&.step10 {
		background: linear-gradient(0deg, rgba(61, 79, 130, 1) -10%, rgba(173, 194, 224, 1) 60%, rgba(205, 223, 250, 1) 80%, rgba(222, 236, 255, 1) 120%);
	}
`;

export const Ocean1 = styled.div`
	background: linear-gradient(0deg, rgba(17, 19, 58, 1) 0%, rgba(173, 61, 106, 1) 100%);
	opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
`;

export const Ocean2 = styled.div`
	background: linear-gradient(0deg,rgba(17, 19, 58, 1) 0%, rgba(38, 38, 82, 1) 35%, rgba(224, 108, 70, 1) 100%);
	opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
`;

export const Ocean3 = styled.div`
	background: linear-gradient(0deg, rgba(61, 79, 130, 1) 28%, rgba(214, 120, 86, 1) 100%);
	opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
`;

export const Ocean4 = styled.div`
	background: linear-gradient(0deg, rgba(61, 79, 130, 1) 28%, rgba(214, 120, 86, 1) 100%);
	opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
`;

export const Ocean5 = styled.div`
	background: linear-gradient(0deg, rgba(61, 79, 130, 1) 0%, rgba(244, 223, 209, 1) 100%);
	opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
`;

export const Ocean6 = styled.div`
	background: linear-gradient(0deg, rgba(61, 79, 130, 1) 0%, rgba(173, 194, 224, 1) 80%, rgba(244, 223, 209, 1) 100%);
	opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
`;

export const Ocean7 = styled.div`
	background: linear-gradient(0deg, rgba(61, 79, 130, 1) 0%, rgba(173, 194, 224, 1) 60%, rgba(205, 223, 250, 1) 80%, rgba(255, 234, 219, 1) 104%);
	opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
`;

export const Ocean8 = styled.div`
	background: linear-gradient(0deg, rgba(61, 79, 130, 1) -10%, rgba(173, 194, 224, 1) 60%, rgba(205, 223, 250, 1) 80%, rgba(255, 234, 219, 1) 120%);
	opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
`;

export const Ocean9 = styled.div`
	background: linear-gradient(0deg, rgba(61, 79, 130, 1) -10%, rgba(173, 194, 224, 1) 60%, rgba(205, 223, 250, 1) 80%, rgba(222, 236, 255, 1) 120%);
	opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
`;

export const Ocean10 = styled.div`
	background: linear-gradient(0deg, rgba(61, 79, 130, 1) -10%, rgba(173, 194, 224, 1) 60%, rgba(205, 223, 250, 1) 80%, rgba(222, 236, 255, 1) 120%);
	opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
`;
